import { useCallback, useMemo } from 'react';
import { useCurrentUser } from 'store/user';
import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { toIssueOnTableView } from 'components/dataProviders/withIssues/model';
import { useGenerateForms } from 'components/issue/useGenerateForms';
import { useProject } from 'store/project';
import { getProcesses } from 'store/process';
import { useCompanies } from 'store/company';
import { useContracts } from 'store/contract';
import { getLevelsStoreState } from 'store/level';
import { getSitesStoreState } from 'store/site';
import type { Store } from 'hooks/createStore';
import { useLatestRef } from 'hooks/useLatestRef';
import { issueClient } from 'shared/domain/issue/issueClient';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { transformArrayToHashMap } from 'shared/utils/array';
import { waitForIssueDictionaries } from '../models/issueDictionaries';
import { useIssuesQuery } from './useIssuesQuery';

export interface FilteredIssuesResult {
  items: IssueOnSingleView[];
  /** Raw API models (events/emails are stripped in `toIssueOnTableView`). */
  issueModels: IssueModel[];
  total: number;
}

export function useGetAllFilteredIssues(): () => Promise<FilteredIssuesResult> {
  const { timezone, organizationId } = useProject();
  const { _id: currentUserId } = useCurrentUser();

  const query = useIssuesQuery();

  const {
    items: contractsArray,
  } = useContracts();
  const {
    items: companiesArray,
  } = useCompanies();

  const {
    forms,
    loadingStore: loadingFormsStore,
  } = useGenerateForms();

  const contracts = useMemo(() => {
    return transformArrayToHashMap(contractsArray);
  }, [contractsArray]);

  const companies = useMemo(() => {
    return transformArrayToHashMap(companiesArray);
  }, [companiesArray]);

  // What the table view maps with, as of the latest render: the report waits below, and renders in between change it.
  // The sites, levels and processes are read from their stores after the wait, as they are the ones it waits for.
  const latestInputs = useLatestRef({ currentUserId, forms, companies, contracts, timezone, organizationId });

  return useCallback(async (): Promise<FilteredIssuesResult> => {
    if (!query) {
      throw new Error('The project is not loaded yet');
    }
    const { items: issueModels } = await issueClient.getAllFiltered(query);
    // A report started right after opening waits for what the issues are mapped with.
    await waitForIssueDictionaries();
    await waitUntilFormsLoaded(loadingFormsStore);

    const { currentUserId, forms, companies, contracts, timezone, organizationId } = latestInputs.current;
    const { items: sites } = getSitesStoreState().data;
    const { items: levels } = getLevelsStoreState().data;
    const processes = getProcesses();
    return {
      issueModels,
      items: issueModels.map((issue) =>
        toIssueOnTableView(
          issue,
          currentUserId,
          sites,
          levels,
          processes,
          forms,
          companies,
          contracts,
          timezone,
          organizationId
        )
      ),
      total: issueModels.length,
    };
  }, [query, loadingFormsStore, latestInputs]);
}

/** The table view needs the issue forms; a report started right after opening waits for them. */
function waitUntilFormsLoaded(loadingFormsStore: Store<boolean>): Promise<void> {
  if (!loadingFormsStore.get()) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const unsubscribe = loadingFormsStore.subscribe(() => {
      unsubscribe();
      resolve();
    });
  });
}
